import { createNewMarker } from "../../../hooks/useGroupSelectionV3/utils";
import { bulkAddPlanElements } from "../../../utils/bulkAddPlanElements";
import { bulkMovePlanElements } from "../../../utils/bulkMovePlanElements";
import { bulkRemovePlanElements } from "../../../utils/bulkRemovePlanElements";
import { removeElementGroupFromPlan } from "../../../utils/removeElementGroupFromPlan";
import { renameGroupInPlan } from "../../../utils/renameGroupInPlan";
import { resolveGroupColor } from "../../../utils/resolveGroupColor";
import { setElementGroupLvMatchInPlan } from "../../../utils/setElementGroupLvMatchInPlan";
import { setElementTypeNameStatusInPlan } from "../../../utils/setElementTypeNameStatusInPlan";
import { updatePlanElement } from "../../../utils/updatePlanElement";
import { markerNoteOfSnapshot } from "../markerNoteOfSnapshot";
import type { BboxRect } from "components/SmartSurveyor/ReviewPage/types";
import type { MarkerNote } from "components/SmartSurveyor/SmartSurveyorForRudolfFritz/utils/types";
import type { BulkAddPlanElementEntry } from "../../../utils/bulkAddPlanElements/types";
import type { ElementMarkerChange, ElementMarkerSnapshot } from "../../types";
import type { PlanV3 } from "../../../utils/types";
import { LV_MATCH_COLUMNS } from "./constants";
import type { PlanPatch } from "./types";

const isMove = ({ before, after }: ElementMarkerChange): boolean =>
  before !== null &&
  after !== null &&
  before.elementTypeId !== after.elementTypeId;

const isReshaped = ({ before, after }: ElementMarkerChange): boolean =>
  before !== null &&
  after !== null &&
  (before.bboxX1 !== after.bboxX1 ||
    before.bboxY1 !== after.bboxY1 ||
    before.bboxX2 !== after.bboxX2 ||
    before.bboxY2 !== after.bboxY2 ||
    before.multiplier !== after.multiplier);

export const pageIndexOf = (plan: PlanV3, pageId: number): number | null => {
  for (const [pageIndex, id] of plan.pageIdByIndex) {
    if (id === pageId) return pageIndex;
  }
  return null;
};

const bboxOf = (marker: ElementMarkerSnapshot): BboxRect => ({
  x1: marker.bboxX1,
  y1: marker.bboxY1,
  x2: marker.bboxX2,
  y2: marker.bboxY2,
});

export const removeDeletedMarkers: PlanPatch = ({
  plan,
  markers,
  groupLabelByElementId,
}) => {
  const elementIds = markers
    .filter((change) => change.changeType === "deleted")
    .map((change) => String(change.rowId));
  if (elementIds.some((id) => !groupLabelByElementId.has(id))) return null;
  return bulkRemovePlanElements({ plan, elementIds });
};

export const moveMarkers: PlanPatch = ({
  plan,
  markers,
  labelByTypeId,
  groupLabelByElementId,
}) => {
  const elementIdsByTypeId = new Map<number, string[]>();
  for (const change of markers) {
    if (change.changeType !== "updated" || change.after === null) continue;
    if (!isMove(change)) continue;
    const typeId = change.after.elementTypeId;
    const elementId = String(change.rowId);
    if (!groupLabelByElementId.has(elementId)) return null;
    const elementIds = elementIdsByTypeId.get(typeId);
    if (elementIds === undefined) elementIdsByTypeId.set(typeId, [elementId]);
    else elementIds.push(elementId);
  }

  let next = plan;
  for (const [typeId, elementIds] of elementIdsByTypeId) {
    const toGroupLabel = labelByTypeId.get(typeId);
    if (toGroupLabel === undefined) return null;
    next = bulkMovePlanElements({
      plan: next,
      elementIds,
      toGroupLabel,
      newGroupElementTypeId: typeId,
    });
  }
  return next;
};

export const addCreatedMarkers: PlanPatch = ({
  plan,
  markers,
  labelByTypeId,
  groupLabelByElementId,
}) => {
  const elements: BulkAddPlanElementEntry[] = [];
  const rgbByLabel = new Map<string, [number, number, number]>();

  for (const { changeType, rowId, after } of markers) {
    if (changeType !== "created" || after === null) continue;
    // Still on screen means this copy of the plan is stale; adding it would show it twice.
    if (groupLabelByElementId.has(String(rowId))) return null;

    const groupLabel = labelByTypeId.get(after.elementTypeId);
    const pageIndex = pageIndexOf(plan, after.pageId);
    if (groupLabel === undefined || pageIndex === null) return null;

    const rgb =
      rgbByLabel.get(groupLabel) ??
      resolveGroupColor({
        groupName: groupLabel,
        byGroupId: plan.groupedElements.byGroupId,
      });
    rgbByLabel.set(groupLabel, rgb);

    elements.push({
      elementTypeId: after.elementTypeId,
      groupLabel,
      element: createNewMarker({
        markerId: String(rowId),
        bboxX1: after.bboxX1,
        bboxY1: after.bboxY1,
        bboxX2: after.bboxX2,
        bboxY2: after.bboxY2,
        multiplier: after.multiplier,
        // Plans load their elements unscaled; see fetchSingleFloorPlan.
        scaleFactor: 1,
        pageIndex,
        // A floor plan has one page, so the plan image is the marker's page image.
        floorPlanImage: plan.planImage,
        elementTypeName: groupLabel,
        rgb,
      }),
    });
  }

  return elements.length === 0 ? plan : bulkAddPlanElements({ plan, elements });
};

// TODO: batch per group like applyNoteChanges; one updatePlanElement per marker is O(markers × group size).
export const editMarkers: PlanPatch = ({
  plan,
  markers,
  labelByTypeId,
  groupLabelByElementId,
}) => {
  let next = plan;
  for (const change of markers) {
    if (change.changeType !== "updated" || change.after === null) continue;
    // A bulk move only changes the type; touching thousands of untouched markers here would stall the page.
    if (isMove(change) && !isReshaped(change)) continue;
    const elementId = String(change.rowId);
    // One edit can move a marker and reshape it; moveMarkers already put it in its new group.
    const fromGroupLabel = isMove(change)
      ? labelByTypeId.get(change.after.elementTypeId)
      : groupLabelByElementId.get(elementId);
    if (fromGroupLabel === undefined) return null;
    next = updatePlanElement({
      plan: next,
      fromGroupLabel,
      elementId,
      changes: {
        bbox: bboxOf(change.after),
        multiplier: change.after.multiplier,
      },
    });
  }
  return next;
};

export const groupLabelByElementIdOf = (
  plan: PlanV3,
): ReadonlyMap<string, string> => {
  const labels = new Map<string, string>();
  for (const [label, elements] of Object.entries(
    plan.groupedElements.byGroupId,
  )) {
    for (const element of elements) labels.set(element.id, label);
  }
  return labels;
};

// One pass per touched group: restoring thousands of notes one by one would copy the group once per note.
export const applyNoteChanges: PlanPatch = ({ plan, notes }) => {
  if (notes.length === 0) return plan;
  // Read from the patched plan: a marker restored by this same entry is not in the context's lookup.
  const groupLabelByElementId = groupLabelByElementIdOf(plan);

  const notesByGroupLabel = new Map<string, Map<string, MarkerNote | null>>();
  for (const { before, after } of notes) {
    const row = after ?? before;
    if (row === null) continue;
    // The marker was deleted right after the revert, so the cached plan is stale: reload.
    if (row.markerId === null) return null;
    const elementId = String(row.markerId);
    const groupLabel = groupLabelByElementId.get(elementId);
    if (groupLabel === undefined) {
      // The marker left with its note in this entry; a note for a marker we never had means a stale plan.
      if (after === null) continue;
      return null;
    }
    const groupNotes =
      notesByGroupLabel.get(groupLabel) ?? new Map<string, MarkerNote | null>();
    groupNotes.set(
      elementId,
      after === null ? null : markerNoteOfSnapshot({ snapshot: after }),
    );
    notesByGroupLabel.set(groupLabel, groupNotes);
  }
  if (notesByGroupLabel.size === 0) return plan;

  // Untouched groups and markers keep their references, so React re-renders only what changed.
  const byGroupId = { ...plan.groupedElements.byGroupId };
  for (const [groupLabel, groupNotes] of notesByGroupLabel) {
    byGroupId[groupLabel] = (byGroupId[groupLabel] ?? []).map((element) => {
      const note = groupNotes.get(element.id);
      return note === undefined ? element : { ...element, note };
    });
  }
  return { ...plan, groupedElements: { ...plan.groupedElements, byGroupId } };
};

// A removed type whose markers left with it is already gone from the plan; this covers the rest.
export const dropRemovedTypes: PlanPatch = ({ plan, elementTypes }) => {
  let next = plan;
  for (const { changeType, before } of elementTypes) {
    if (changeType !== "deleted" || before === null) continue;
    const groupLabel = before.name;
    if (!(groupLabel in next.groupMeta)) continue;
    if ((next.groupedElements.byGroupId[groupLabel] ?? []).length > 0) continue;
    next = removeElementGroupFromPlan({ plan: next, groupLabel });
  }
  return next;
};

export const applyTypeChanges: PlanPatch = ({ plan, elementTypes }) => {
  let next = plan;
  for (const { changeType, rowId, before, after } of elementTypes) {
    if (after === null) continue;
    // Only a restored type with no markers is still missing; its empty group is not rebuilt here.
    if (changeType === "created" && !(after.name in next.groupMeta)) {
      return null;
    }
    const isRenamed =
      changeType === "updated" && before !== null && before.name !== after.name;
    if (isRenamed) {
      next = renameGroupInPlan({
        plan: next,
        oldGroupLabel: before.name,
        newGroupLabel: after.name,
        nameStatus: after.nameStatus,
        elementTypeId: rowId,
      });
    }
    // Groups that the marker patches created were given "confident"; this sets what the row says.
    next = setElementTypeNameStatusInPlan({
      plan: next,
      elementTypeIds: [rowId],
      nameStatus: after.nameStatus,
    });
    // A restored group was rebuilt without its LV match and an LV edit may be undone; the row carries the value.
    const isLvMatchChanged =
      before === null
        ? after.lvMatchId !== null
        : LV_MATCH_COLUMNS.some((column) => before[column] !== after[column]);
    if (isLvMatchChanged) {
      next = setElementGroupLvMatchInPlan({
        plan: next,
        elementGroupLabel: after.name,
        lvMatch: {
          lvId: after.lvMatchId ?? undefined,
          lvName: after.lvMatchName ?? undefined,
          reasoning: after.lvMatchReasoning ?? undefined,
        },
      });
    }
  }
  return next;
};
